<template>
  <div class="container-lg  mt-2">

    <catalog-breadcrumbs v-if="isCategoryDefined" :category="category" :in18="in18"/>

    <div class="grid grid--category">
      <div class="grid__sidebar">
        <aside class="sidebar">

          <catalog-benefits
              :in18="in18"
              :catalog_banner="catalog_banner"
          />

          <catalog-filters
              v-if="filtersAvailable"
              :filters="filters"
              :in18="in18"
              :isLoading="isLoading"
              :params="params"
              @change-filter="handleFilterChange"
              @clear-filter="handleClearFilter"
          />

        </aside>
      </div><!-- /.grid__sidebar -->

      <div class="grid__category-header">

        <catalog-subcategories v-if="isCategoryDefined" :filters="filters" :in18="in18"/>

        <div class="section__header  mt-5  mb-5" id="section_goods_id">

          <div class="row  align-items-center">
            <div class="col-md-5  order-1">
              <h1 v-if="isCategoryDefined" class="section__title  mb-0">
                {{ category.title }}
              </h1>
              <h2 v-else>
                {{ manufacture.title }} {{ in18['page_products'] }}
              </h2>
            </div>

            <div class="col-md-7  order-3  order-md-2">
              <catalog-sort
                  :display="display"
                  :in18="in18"
                  @change-filter="handleFilterChange"
                  @display-changed="handleDisplayModeChange"
              />
            </div>

            <div v-if="isCategoryDefined && category.short_description" class="col order-2 order-md-3">
              <div class="category-text">
                <div :class="{'expanded': showMore }" class="category-description">
                  <p>{{ displayedDescription }}</p>
                </div>
                <p v-if="showMoreButton">
                  <a class="link link--dark fw-500 hide-button" href="#" @click.prevent="toggleText">
                    {{ showMore ? in18['page_read_less'] : in18['page_read_more'] }}
                  </a>
                </p>
              </div>
            </div>
          </div><!-- /.row -->
        </div><!-- /.section__header -->
      </div><!-- /.grid__category-header -->

      <div class="grid__category-main">
        <div class="row">
          <catalog-products
              :in18="in18"
              :display="display"
              :isLoading="isLoading"
              :pagination="pagination"
              :products="products"
              @change-page="handlePageChange"
          />
        </div>
      </div><!-- /.grid__category-main -->

    </div><!-- /.grid -->
  </div><!-- /.container-lg -->
</template>

<script>
import axios from "axios";
import CatalogBreadcrumbs from "../components/catalog/CatalogBreadcrumbs.vue";
import CatalogSort from "../components/catalog/CatalogSort.vue";
import CatalogBenefits from "../components/catalog/CatalogBenefits.vue";
import CatalogFilters from "../components/catalog/CatalogFilters.vue";
import CatalogProducts from "../components/catalog/CatalogProducts.vue";
import CatalogSubcategories from "../components/catalog/CatalogSubcategories.vue";
import {DEFAULT_CATALOG_PARAMS} from '../config';

export default {
  name:       "Catalog",
  components: {
    CatalogBreadcrumbs,
    CatalogSort,
    CatalogFilters,
    CatalogProducts,
    CatalogSubcategories,
    CatalogBenefits,
  },
  props:      {
    subcategories:  Object,
    category:       Object | null,
    manufacture:    Object | null,
    query_params:   Object,
    in18:           Object,
    catalog_banner: Object,
  },
  created() {
    // Load filters from query parameters and fetch filtered products on component creation.
    this.updateFiltersFromQuery();
    this.fetchFilteredProducts();
  },

  computed: {
    isCategoryDefined() {
      return this.category !== undefined && this.category !== null;
    },
    filtersAvailable() {
      // Check if filters are available (not empty).
      return !this.filters || Object.keys(this.filters).length > 0;
    },
    displayedDescription() {
      if (this.category.short_description.length > this.maxDescriptionLength) {
        return this.showMore
            ? this.category.short_description
            : this.category.short_description.slice(0, this.maxDescriptionLength) + '...';
      } else {
        return this.category.short_description;
      }
    },
    showMoreButton() {
      return this.category.short_description.length > this.maxDescriptionLength;
    },
    display: {
      get() {
        return this.$store.getters.catalogDisplayMode
      },
      set(value) {
        this.$store.commit('SET_CATALOG_DISPLAY_MODE', value)
      },
    },
  },

  data() {
    return {
      showMore:             false,
      maxDescriptionLength: 500,
      isLoading:            true,
      products:             {},
      filters:              {},
      params:               {
        ...DEFAULT_CATALOG_PARAMS,
      },
      pagination:           {
        links: {},
        meta:  {},
      },
    };
  },

  methods: {
    /**
     * Update filters from query parameters in the URL.
     */
    updateFiltersFromQuery() {
      const queryParams = new URLSearchParams(window.location.search);

      // TODO :: Need to refactor this section.
      this.params = {
        page:         parseInt(queryParams.get("page")) || 1,
        per_page:     parseInt(queryParams.get("per_page")) || 32,
        sort:         queryParams.get("sort") || null,
        categories:   queryParams.get("categories")
                          ? queryParams.get("categories").split(",")
                          : [],
        manufactures: queryParams.get("manufactures")
                          ? queryParams.get("manufactures").split(",")
                          : [],
        inventories:  queryParams.get("inventories")
                          ? queryParams.get("inventories").split(",")
                          : [],
        properties:   queryParams.get("properties")
                          ? queryParams.get("properties").split(",")
                          : [],
        discounts:    queryParams.get("discounts")
                          ? queryParams.get("discounts").split(",")
                          : [],
        // price_from:   parseInt(queryParams.get("price_from")) || 0,
        // price_to:     parseInt(queryParams.get("price_to")) || 0,
      };
    },

    /**
     * Fetch filtered products based on category and query parameters.
     */
    fetchFilteredProducts() {

      // Show loading indicator.
      this.isLoading = true;
      // Combine default and query parameters.
      const params = {...this.params, ...this.query_params};

      axios
          .get("/api/search/products", {params})
          .then((response) => {
            this.products = response.data.collection.data.products;
            this.pagination = {
              links: response.data.collection.links,
              meta:  response.data.collection.meta,
            };
            this.filters = response.data.filters;
            ga_view_item_list_event(response.data.collection.data.products);
          })
          .catch((error) => {
            console.error(error.response);
            window.toastr.error(error.response.data.message);
          })
          .finally(() => {
            // Hide loading indicator.
            this.isLoading = false;
          });

    },


    /**
     * Handle a change in filter criteria.
     *
     * This method is used to update the filter criteria and trigger a product
     * filtering operation. It sets the page to 1, updates the specified filter
     * property, updates the URL, and fetches filtered products.
     *
     * @param {string} prop - The filter property to change.
     * @param {any} value - The new value for the filter property.
     */
    handleFilterChange(prop, value) {
      this.params.page = 1;
      this.params[prop] = value;
      window.updateURL(this.params);
      this.fetchFilteredProducts();
      this.scrollCatalogToTop();
    },

    /**
     * Handle a change in the current page.
     *
     * This method is used to update the current page number and trigger a product
     * pagination operation. It updates the page number, updates the URL, and
     * fetches products for the new page.
     *
     * @param {number} page - The new page number.
     */
    handlePageChange(page) {
      this.params.page = page;
      window.updateURL(this.params);
      this.fetchFilteredProducts();
      this.scrollCatalogToTop();
    },

    /**
     * Handle clearing all filter criteria to their default values.
     *
     * This method is used to reset all filter criteria to their default values,
     * update the URL, and fetch products with the default filters.
     */
    handleClearFilter() {
      // Reset filters to default values, update the URL, and fetch products.
      this.params = {};
      window.updateURL(this.params);
      this.params = {...DEFAULT_CATALOG_PARAMS};
      this.fetchFilteredProducts();
      this.scrollCatalogToTop();
    },

    /**
     * Handle a change in the display mode.
     * @param {string} display - The new display mode (e.g., 'grid' or 'list').
     */
    handleDisplayModeChange(display) {
      this.display = display;
    },

    toggleText() {
      this.showMore = !this.showMore;
    },


    scrollCatalogToTop() {

      const container = $('#section_goods_id');

      if (container.isInViewport()) {
        return;
      }

      const mobile_filter = $('.dropdown__toggle--filter');

      if (mobile_filter.isInViewport()) {
        return;
      }

      const category = $('.category');

      const topPosition = category.outerHeight(true) ?
          container.offset().top - container.outerHeight(true)
          : container.offset().top - 170;

      $('html, body').animate({scrollTop: topPosition}, 1000);
    },
  },
};
</script>

<style scoped>

.category-description {
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
}

.category-description.expanded {
  -webkit-line-clamp: 10;
}

</style>
